import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Instagram, Facebook, MapPin, Phone, Mail } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useCmsDoc } from '../../hooks/useCms';
import { FALLBACK_SETTINGS, FALLBACK_CONTACT } from '../../data/cmsFallbacks';
import { translations } from '../../data/translations';

const Footer = () => {
  const { t } = useLanguage();
  const settings = useCmsDoc('/api/cms/settings', FALLBACK_SETTINGS);
  const contact = useCmsDoc('/api/cms/contact-info', FALLBACK_CONTACT);
  
  // Navigation links with translations
  const navigationLinks = [
    { id: 1, name: t('about', translations.nav), path: '/about' },
    { id: 2, name: t('services', translations.nav), path: '/services' },
    { id: 3, name: t('news', translations.nav), path: '/news' },
    { id: 4, name: t('partners', translations.nav), path: '/partners' }
  ];

  const services = [
    { de: 'Housekeeping', en: 'Housekeeping' },
    { de: 'Front Office', en: 'Front Office' },
    { de: 'Fensterreinigung', en: 'Window Cleaning' },
    { de: 'Bankett-Service', en: 'Banquet Service' }
  ];

  return (
    <footer className="relative">
      {/* Middle Section - Black Background with Logo + Navigation Grid */}
      <div className="bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          {/* Desktop Layout */}
          <div className="hidden md:flex items-start gap-0">
            {/* Logo Column - Extreme left */}
            <div className="flex flex-col items-start flex-shrink-0 -mt-2">
              <Link to="/">
                <img 
                  src={settings.logo_url || FALLBACK_SETTINGS.logo_url}
                  alt="Yetkin Dienstleistungen"
                  className="h-52 w-auto brightness-0 invert"
                />
              </Link>
            </div>

            {/* Vertical Golden Glow Line - aligned with Navigation columns */}
            <div className="relative w-[2px] bg-black overflow-hidden mx-8 self-stretch">
              <div 
                className="absolute left-0 w-full h-[100px]"
                style={{
                  background: 'linear-gradient(180deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
                  animation: 'verticalGlow 4s ease-in-out infinite',
                  boxShadow: '0 0 20px #D4AF37, 0 0 40px #D4AF37',
                }}
              />
            </div>

            {/* Navigation Grid */}
            <div className="flex-1 grid grid-cols-3 gap-12">
              {/* Navigation */}
              <div className="text-left">
                <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-6">{t('navigation', translations.footer)}</h4>
                <ul className="space-y-3">
                  {navigationLinks.map((link) => (
                    <li key={link.id}>
                      <Link to={link.path} className="text-white/60 text-sm hover:text-[#D4AF37] transition-colors">{link.name}</Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Services */}
              <div className="text-left">
                <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-6">{t('servicesTitle', translations.footer)}</h4>
                <ul className="space-y-3">
                  {services.map((s, i) => (
                    <li key={i}><Link to="/services" className="text-white/60 text-sm hover:text-[#D4AF37] transition-colors cursor-pointer">{t('name', s)}</Link></li>
                  ))}
                </ul>
              </div>

              {/* Contact */}
              <div className="text-left">
                <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-6">{t('contactTitle', translations.footer)}</h4>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3"><MapPin size={18} className="text-[#D4AF37] mt-0.5 flex-shrink-0" /><span className="text-white/60 text-sm">{contact.address}</span></li>
                  <li className="flex items-center gap-3"><Phone size={18} className="text-[#D4AF37] flex-shrink-0" /><span className="text-white/60 text-sm">{contact.phone}</span></li>
                  <li className="flex items-center gap-3"><Mail size={18} className="text-[#D4AF37] flex-shrink-0" /><span className="text-white/60 text-sm">{contact.email}</span></li>
                </ul>
              </div>
            </div>

            {/* Social Links - Vertical column on extreme right */}
            <div className="flex flex-col gap-4 ml-auto pl-8">
              {[Linkedin, Instagram, Facebook].map((Icon, i) => (
                <a key={i} href={[settings.linkedin_url, settings.instagram_url, settings.facebook_url][i] || '#'} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all duration-300">
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden flex flex-col items-center">
            {/* Logo - Centered at top */}
            <div className="flex flex-col items-center mb-8">
              <Link to="/">
                <img 
                  src={settings.logo_url || FALLBACK_SETTINGS.logo_url}
                  alt="Yetkin Dienstleistungen"
                  className="h-40 w-auto brightness-0 invert"
                />
              </Link>
            </div>

            {/* Horizontal Golden Glow Line */}
            <div className="relative h-[2px] w-full bg-black overflow-hidden mb-8">
              <div 
                className="absolute top-0 h-full w-[150px]"
                style={{
                  background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
                  animation: 'horizontalGlow 4s ease-in-out infinite',
                  boxShadow: '0 0 20px #D4AF37, 0 0 40px #D4AF37',
                }}
              />
            </div>

            {/* Navigation Grid - Stacked columns */}
            <div className="grid grid-cols-1 gap-10 w-full text-center">
              {/* Navigation */}
              <div>
                <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">{t('navigation', translations.footer)}</h4>
                <ul className="space-y-2">
                  {navigationLinks.map((link) => (
                    <li key={link.id}>
                      <Link to={link.path} className="text-white/60 text-sm hover:text-[#D4AF37] transition-colors">{link.name}</Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Services */}
              <div>
                <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">{t('servicesTitle', translations.footer)}</h4>
                <ul className="space-y-2">
                  {services.map((s, i) => (
                    <li key={i}><Link to="/services" className="text-white/60 text-sm hover:text-[#D4AF37] transition-colors cursor-pointer">{t('name', s)}</Link></li>
                  ))}
                </ul>
              </div>

              {/* Contact */}
              <div>
                <h4 className="text-white font-semibold text-sm uppercase tracking-wider mb-4">{t('contactTitle', translations.footer)}</h4>
                <ul className="space-y-3">
                  <li className="flex items-center gap-3 justify-center"><MapPin size={18} className="text-[#D4AF37] flex-shrink-0" /><span className="text-white/60 text-sm">{contact.address}</span></li>
                  <li className="flex items-center gap-3 justify-center"><Phone size={18} className="text-[#D4AF37] flex-shrink-0" /><span className="text-white/60 text-sm">{contact.phone}</span></li>
                  <li className="flex items-center gap-3 justify-center"><Mail size={18} className="text-[#D4AF37] flex-shrink-0" /><span className="text-white/60 text-sm">{contact.email}</span></li>
                </ul>
              </div>
            </div>

            {/* Social Links - Horizontal row */}
            <div className="flex gap-4 mt-10">
              {[Linkedin, Instagram, Facebook].map((Icon, i) => (
                <a key={i} href={[settings.linkedin_url, settings.instagram_url, settings.facebook_url][i] || '#'} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full border border-[#D4AF37] flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-all duration-300">
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section - Copyright */}
      <div className="bg-black border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-sm">{t('c', { de: settings.copyright_de, en: settings.copyright_en }) || t('copyright', translations.footer)}</p>
            <div className="flex gap-6">
              <Link to="/datenschutz" className="text-white/40 text-sm hover:text-[#D4AF37] transition-colors">{t('privacyPolicy', translations.footer)}</Link>
              <Link to="/impressum" className="text-white/40 text-sm hover:text-[#D4AF37] transition-colors">{t('legalNotice', translations.footer)}</Link>
            </div>
          </div>
        </div>
      </div>

      {/* Glow Animations */}
      <style>{`
        @keyframes verticalGlow {
          0% {
            top: -100px;
            opacity: 0;
          }
          15% {
            opacity: 1;
          }
          85% {
            opacity: 1;
          }
          100% {
            top: 100%;
            opacity: 0;
          }
        }
        @keyframes horizontalGlow {
          0% {
            left: -150px;
            opacity: 0;
          }
          15% {
            opacity: 1;
          }
          85% {
            opacity: 1;
          }
          100% {
            left: 100%;
            opacity: 0;
          }
        }
      `}</style>
    </footer>
  );
};

export default Footer;
